import React from 'react';
import { toast } from 'react-hot-toast';
import { numberToColumnLetter, getCellKey, getColumnIndex } from '../utils';
import { SpreadsheetData } from '../types';

interface UseSpreadsheetSelectAllProps {
  spreadsheetData: SpreadsheetData[];
  customColumnHeaders?: string[];
  onSelectAll?: (allCells: Set<string>) => void;
}

export function useSpreadsheetSelectAll({
  spreadsheetData,
  customColumnHeaders,
  onSelectAll
}: UseSpreadsheetSelectAllProps) {
  // Select all cells in the spreadsheet
  const selectAllCells = React.useCallback(() => {
    // Determine actual data bounds
    const actualRows = Math.max(spreadsheetData.length, 1); // Ensure at least 1 row
    
    let maxColumnIndex = 0;
    
    // If we have custom column headers, use them to determine the column range
    if (customColumnHeaders && customColumnHeaders.length > 0) {
      maxColumnIndex = customColumnHeaders.length - 1;
    } else {
      // Find the maximum column index that has meaningful (non-empty) data
      spreadsheetData.forEach(row => {
        Object.keys(row).forEach(key => {
          if (key !== '__rowNumber') { // Skip special row number key
            const columnIndex = getColumnIndex(key);
            if (columnIndex !== -1) {
              // Only consider this column if it has non-empty data
              const hasData = spreadsheetData.some(r => {
                const value = r[key];
                return value !== null && value !== undefined && value !== '';
              });
              
              if (hasData && columnIndex > maxColumnIndex) {
                maxColumnIndex = columnIndex;
              }
            }
          }
        });
      });
    }
    
    // Ensure at least column A (index 0) is included
    maxColumnIndex = Math.max(maxColumnIndex, 0);
    const actualColumns = maxColumnIndex + 1; // Convert from 0-based index to count
    
    const allCells = new Set<string>();
    
    // Generate all cell keys for the actual data bounds
    for (let rowIdx = 0; rowIdx < actualRows; rowIdx++) {
      for (let colIdx = 0; colIdx <= maxColumnIndex; colIdx++) {
        const columnKey = numberToColumnLetter(colIdx);
        const cellKey = getCellKey(rowIdx, columnKey);
        allCells.add(cellKey);
      }
    }

    // Call the callback to update selection
    if (onSelectAll) {
      onSelectAll(allCells);
    }

    // Show toast notification
    toast.success(`Selected all cells (${actualRows} × ${actualColumns})`);

    console.log('Selected all cells:', allCells.size, 'cells');
  }, [spreadsheetData, customColumnHeaders, onSelectAll]);

  // Handle select all operations within the spreadsheet container
  const handleSelectAllKeyDown = React.useCallback((event: React.KeyboardEvent, activeElement?: Element) => {
    // Handle select all operation
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      // Check if the active element is an input within our spreadsheet
      const isInputInSpreadsheet = activeElement && 
        activeElement.tagName === 'INPUT';
      
      // If it's an input in our spreadsheet and there's text selected, let browser handle it
      if (isInputInSpreadsheet) {
        const input = activeElement as HTMLInputElement;
        // If there's text in the input and nothing is selected, or if there's a selection, let browser handle it
        if (input.value && (input.selectionStart !== input.selectionEnd || input.value.length > 0)) {
          return false; // Let browser handle text selection
        }
      }
      
      // Otherwise, handle spreadsheet select all
      event.preventDefault();
      selectAllCells();
      return true;
    }

    return false;
  }, [selectAllCells]);

  return {
    selectAllCells,
    handleSelectAllKeyDown
  };
} 